import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Bot, Loader2, Search } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { useAuth, useAppAuth } from '@aimsos/auth';
import { useStickyScroll } from '@aimsos/hooks';
import { Pagination, StatusMessage, useToast } from '@aimsos/ui';
import { useChatHostRuntime } from '@aimsos/chat';

import { AgentGridCard, AgentsFiltersSlideout, AgentsListHeader } from '../components';
import { useAgents } from '../context';
import { createAgent } from '../../../services';
import { parseAgentCreateResponse } from '../../../shared/utils/apiResponse';
import { mapAgentListItemToUi } from '../utils';

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const AgentsListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('agents');
  const { user } = useAuth();
  const { userId } = useAppAuth();
  const toast = useToast();
  const hostRuntime = useChatHostRuntime();
  const {
    agents,
    listLoading,
    listError,
    meta,
    setPage,
    pageSize,
    setPageSize,
    listSearchKeyword,
    setListSearchKeyword,
    filters,
    setFilters,
  } = useAgents();

  const [listScrollHover, setListScrollHover] = useState(false);
  const [creatingAgent, setCreatingAgent] = useState(false);

  const rangeLabel = useMemo(() => {
    const total = meta.total_items;
    if (total === 0 || agents.length === 0) return '0-0';
    const start = (meta.current_page - 1) * meta.per_page + 1;
    const end = start + agents.length - 1;
    return `${start}-${end}`;
  }, [meta.current_page, meta.per_page, meta.total_items, agents.length]);

  const handleEdit = useCallback(
    (agent) => navigate(`/agents/${agent.id}`),
    [navigate],
  );

  const handleChat = useCallback(
    (agent) => {
      if (hostRuntime?.openChatWithAgent) {
        hostRuntime.openChatWithAgent(agent);
      } else {
        navigate(`/agents/${agent.id}?tab=chat`);
      }
    },
    [hostRuntime, navigate],
  );

  const handlePrevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), [setPage]);
  const handleNextPage = useCallback(
    () => setPage((p) => (meta.has_next ? p + 1 : p)),
    [setPage, meta.has_next],
  );

  const handleCreate = useCallback(async () => {
    if (creatingAgent) return;
    setCreatingAgent(true);
    try {
      const ownerId = (userId || '').trim();
      if (!ownerId) {
        toast.error({
          title: t('list.createMissingUserTitle'),
          message: t('list.createMissingUserMessage'),
          canClose: true,
        });
        return;
      }
      const username =
        user?.given_name?.trim() ||
        user?.name?.trim() ||
        user?.nickname?.trim() ||
        'User';
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(
        now.getHours(),
      )}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

      const payload = {
        name: `${username} New Agent ${timestamp}`,
        description: '',
        starters: [],
        metadata: {},
        owner: ownerId,
        createdBy: ownerId,
        status: 'active',
        type: 'ai',
        version: 1,
        scope: 'internal',
        tags: '',
        category: 'Service',
      };

      const res = await createAgent(payload);
      const createdRaw = parseAgentCreateResponse(res, t('list.createError'));
      const created = mapAgentListItemToUi(createdRaw);
      if (!created.id) {
        throw new Error(t('list.createMissingUuidError'));
      }
      toast.success({
        title: t('list.createSuccessTitle'),
        message: t('list.createSuccessMessage', { name: created.name || payload.name }),
        canClose: true,
      });
      navigate(`/agents/${created.id}/configuration`);
    } catch (err) {
      toast.error({
        title: t('list.createErrorTitle'),
        message: err?.message ?? t('list.createError'),
        canClose: true,
      });
    } finally {
      setCreatingAgent(false);
    }
  }, [creatingAgent, navigate, t, toast, user, userId]);

  const isInitialLoad =
    listLoading && agents.length === 0 && !listError && !listSearchKeyword.trim();

  const showPagination = !isInitialLoad && !listError && meta.total_items > 0;

  // Committed vs draft: the bar's chips narrow the list immediately, while the
  // "All filters" slideout edits a draft that only reaches `filters` on Apply —
  // so dismissing it discards, and the list never twitches mid-choice.
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);

  const handleOpenAllFilters = useCallback(() => {
    setDraftFilters(filters);
    setIsAllFiltersOpen(true);
  }, [filters]);

  const setDraftField = useCallback(
    (field, value) => setDraftFilters((prev) => ({ ...prev, [field]: value })),
    [],
  );
  const applyDraft = useCallback(() => {
    setFilters(draftFilters);
    setPage(1);
  }, [draftFilters, setFilters, setPage]);
  const clearDraft = useCallback(
    () => setDraftFilters({ status: [], type: [], category: [], createdWithin: '' }),
    [],
  );

  // "Nothing matched" and "nothing here yet" are different screens, and what
  // tells them apart is whether ANYTHING is narrowing — not just the search box.
  // Before the slideout existed a zero-result filter set was hard to reach, so
  // the search-only test held by accident; now "Archived" alone reaches it and
  // the page would offer to create a first agent next to 198 of them.
  const hasQuery =
    Boolean(listSearchKeyword.trim()) ||
    filters.status.length > 0 ||
    filters.type.length > 0 ||
    filters.category.length > 0 ||
    Boolean(filters.createdWithin);

  // `Pagination` composes "<range> of <total> items" in English by default, so
  // the bar has to be told its copy or a Spanish page prints an English footer.
  const paginationLabels = useMemo(
    () => ({
      rowsPerPage: t('list.pagination.rowsPerPage'),
      items: t('list.pagination.items', { range: rangeLabel, total: meta.total_items }),
      previousPage: t('list.pagination.previousPage'),
      nextPage: t('list.pagination.nextPage'),
    }),
    [t, rangeLabel, meta.total_items],
  );

  const scrollRef = useRef(null);
  const { isScrolled, filtersVisible, onMouseEnter, onMouseLeave } = useStickyScroll(scrollRef);
  // At rest the toolbar is already on screen, so the hover pair is armed only
  // while compressed.
  const showToolbar = !isScrolled || filtersVisible;

  // Results only. The header band is rendered by the page and stays put through
  // every one of these states — previously it lived inside this function, so a
  // load or an error took the title, the Create button and the filters with it.
  const renderResults = () => {
    if (isInitialLoad) {
      return (
        <StatusMessage
          icon={Loader2}
          iconClassName="animate-spin"
          title={t('list.loading')}
          subtitle={t('list.loadingDescription')}
        />
      );
    }

    if (listError) {
      return (
        <StatusMessage icon={AlertTriangle} title={t('list.error')} subtitle={listError} />
      );
    }

    if (listLoading) {
      return (
        <StatusMessage
          icon={Loader2}
          iconClassName="animate-spin"
          title={t('list.loading')}
          subtitle={t('list.loadingDescription')}
        />
      );
    }

    if (agents.length === 0) {
      return hasQuery ? (
        <StatusMessage
          icon={Search}
          title={t('list.noResults')}
          subtitle={
            listSearchKeyword.trim()
              ? t('list.noResultsFor', { term: listSearchKeyword.trim() })
              : t('list.noResultsForFilters')
          }
        />
      ) : (
        <StatusMessage
          icon={Bot}
          title={t('list.empty.title')}
          subtitle={t('list.empty.subtitle')}
        />
      );
    }

    return (
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-5 [&>*]:min-w-0">
        {agents.map((agent) => (
          <AgentGridCard key={agent.id} agent={agent} onEdit={handleEdit} onChat={handleChat} />
        ))}
      </div>
    );
  };

  return (
    // Root owns NO scroll: two scrollers and `position: sticky` resolves against
    // the wrong one. See docs/architecture/list-page/.
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-base">
      <div
        ref={scrollRef}
        data-agents-list-scroll
        className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto"
        data-scrollbar-hover={listScrollHover ? 'true' : undefined}
        onMouseEnter={() => setListScrollHover(true)}
        onMouseLeave={() => setListScrollHover(false)}
      >
        {/* `min-h-full` is what lets the footer's `mt-auto` reach the bottom on a
            short list instead of sitting under the last card. */}
        <div className="flex min-h-full flex-col">
          <AgentsListHeader
            searchValue={listSearchKeyword}
            onSearchChange={setListSearchKeyword}
            onSearchClear={() => setListSearchKeyword('')}
            onCreateClick={handleCreate}
            createLoading={creatingAgent}
            filters={filters}
            onFilterChange={(next) => { setFilters(next); setPage(1); }}
            onAllFiltersClick={handleOpenAllFilters}
            isScrolled={isScrolled}
            showToolbar={showToolbar}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          />

          {/* No bottom padding while the pagination shows: a sticky element stays
              pinned only while its flow position is off screen, so padding here
              would un-pin it by exactly that much on the last scroll step. */}
          <div
            className={['flex min-w-0 flex-1 flex-col gap-4 px-6', showPagination ? '' : 'pb-6']
              .filter(Boolean)
              .join(' ')}
          >
            <div className="min-w-0">{renderResults()}</div>

            {showPagination ? (
              <div className="sticky bottom-0 z-10 mt-auto border-t border-edge bg-surface-base pb-6 pt-3">
                <Pagination
                  rangeLabel={rangeLabel}
                  totalItems={meta.total_items}
                  currentPage={meta.current_page}
                  totalPages={meta.total_pages}
                  hasPrevious={meta.has_previous}
                  hasNext={meta.has_next}
                  perPage={pageSize}
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  onPrevPage={handlePrevPage}
                  onNextPage={handleNextPage}
                  onPageSizeChange={setPageSize}
                  disabled={listLoading}
                  labels={paginationLabels}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Overlays are siblings of the scroll container, never inside it — in
          there they inherit its clipping and scroll away with the list. */}
      <AgentsFiltersSlideout
        isOpen={isAllFiltersOpen}
        onClose={() => setIsAllFiltersOpen(false)}
        draft={draftFilters}
        setDraftField={setDraftField}
        applyDraft={applyDraft}
        clearDraft={clearDraft}
      />
    </div>
  );
};

export default AgentsListPage;
